/**
 * Cookie utilities using js-cookie with SSR safety
 */
import Cookies from 'js-cookie'

import {
  AUTH_COOKIE_NAME,
  AUTH_COOKIE_WRITE_OPTIONS,
  clearAuthCookieOnEvent,
  writeAuthCookieOnEvent,
  type ServerRequestEvent,
} from './auth-cookie.server.utils'
import { decodeBase64JsonAuth, type AuthCookieData } from './auth-cookie.utils'

interface CookieAttributes {
  expires?: number | Date
  path?: string
  domain?: string
  secure?: boolean
  sameSite?: 'Strict' | 'Lax' | 'None'
}

export interface CookieOptions {
  path?: string
  expires?: Date | number
  domain?: string
  secure?: boolean
  sameSite?: 'Strict' | 'Lax' | 'None'
}

/**
 * Set a cookie using js-cookie with SSR safety
 */
export function setCookie(name: string, value: string, options: CookieOptions = {}): void {
  if (typeof document === 'undefined') return

  const cookieOptions: CookieAttributes = {
    path: options.path || '/',
    secure: options.secure !== false,
    sameSite: options.sameSite || 'Lax',
  }

  if (options.domain) {
    cookieOptions.domain = options.domain
  }

  if (options.expires) {
    cookieOptions.expires = typeof options.expires === 'number' ? options.expires : options.expires
  }

  Cookies.set(name, value, cookieOptions)
}

/**
 * Get a cookie value using js-cookie with SSR safety
 */
export function getCookie(name: string): string | null {
  if (typeof document === 'undefined') return null
  return Cookies.get(name) || null
}

/**
 * Delete a cookie using js-cookie with SSR safety
 */
export function deleteCookie(name: string, options: CookieOptions = {}): void {
  if (typeof document === 'undefined') return
  Cookies.remove(name, {
    path: options.path || '/',
    domain: options.domain,
  })
}

/**
 * Check if a cookie exists
 */
export function hasCookie(name: string): boolean {
  return getCookie(name) !== null
}

/**
 * Set authentication cookie with proper encoding and validation
 * Supports both client-side and server-side cookie setting
 */
export async function setAuthCookie(data: AuthCookieData, options: CookieOptions = {}, event?: ServerRequestEvent): Promise<void> {
  if (!data.token || !data.contactId) {
    throw new Error('Missing required auth data: token or contactId')
  }

  try {
    const jsonString = JSON.stringify(data)
    const encodedData = typeof btoa !== 'undefined' ? btoa(jsonString) : Buffer.from(jsonString).toString('base64')

    if (process.server) {
      if (event?.node?.res) {
        writeAuthCookieOnEvent(event, encodedData, {
          ...AUTH_COOKIE_WRITE_OPTIONS,
          ...options,
        })
        return
      }

      try {
        const { setAuthCookieViaUseCookie } = await import('./auth-cookie.nuxt.utils')
        setAuthCookieViaUseCookie(encodedData)
        return
      } catch {
        throw new Error('Server auth cookie write requires a request event or Nuxt composable context')
      }
    }

    setCookie(AUTH_COOKIE_NAME, encodedData, {
      path: '/',
      expires: 365,
      secure: true,
      sameSite: 'None',
      ...options,
    })
  } catch (error) {
    console.error('Failed to set auth cookie:', error)
    throw new Error('Failed to encode auth data for cookie')
  }
}

/**
 * Read and parse authentication cookie
 * Note: This only works on the client side. For server-side, use server middleware.
 */
export function getAuthCookie(): AuthCookieData | null {
  const encodedData = getCookie(AUTH_COOKIE_NAME)
  if (!encodedData) {
    return null
  }

  const data = decodeBase64JsonAuth(encodedData)
  if (!data) {
    console.warn('Auth cookie missing essential fields: token or contactId')
  }

  return data
}

/**
 * Clear authentication cookie
 */
export function clearAuthCookie(): void {
  deleteCookie(AUTH_COOKIE_NAME, { path: '/' })
  deleteCookie('cat', { path: '/' })
  deleteCookie('cat-v2', { path: '/' })
}

/**
 * Clear authentication cookie on an SSR response.
 */
export function clearAuthCookieForEvent(event: ServerRequestEvent): void {
  clearAuthCookieOnEvent(event)
}

/**
 * Validate and repair authentication cookie if possible
 */
export function validateAuthCookie(): AuthCookieData | null {
  const data = getAuthCookie()
  if (!data) {
    return null
  }

  if (!data.token || data.token.length < 10) {
    console.warn('Auth cookie has invalid token')
    clearAuthCookie()
    return null
  }

  return data
}

/**
 * Get specific auth data field
 */
export function getAuthField<K extends keyof AuthCookieData>(field: K): AuthCookieData[K] | null {
  const data = getAuthCookie()
  return data ? data[field] : null
}

/**
 * Check if user is authenticated based on cookie
 */
export function isAuthenticatedFromCookie(): boolean {
  const data = getAuthCookie()
  return !!(data?.token && data?.contactId)
}
